import {
    LuArrowLeftRight,
    LuArrowUpRight,
    LuCalendarClock,
    LuPercent,
    LuPiggyBank,
    LuRocket,
    LuScissors,
} from 'react-icons/lu';
import { Link } from 'react-router-dom';

import { ManagementType, TradingStatus } from '@ndvr/portfolio-api';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { formatCurrency, formatDate, titleCase } from 'utils';
import type { NextWithdrawal } from 'views/portfolio/PortfolioPage/tabs/liquidity-planning/cash-flow-schedule';

interface TradingOverviewCardProps {
    managementType?: ManagementType;
    tradingStatus?: TradingStatus;
    cash?: string;
    harvestableLoss?: string;
    ytdShortTermGains?: number;
    ytdLongTermGains?: number;
    nextWithdrawal?: NextWithdrawal;
    isTaxMetricsLoading: boolean;
    isWithdrawalLoading: boolean;
    tradingPath: string;
}

export const TradingOverviewCard = ({
    managementType,
    tradingStatus,
    cash,
    harvestableLoss,
    ytdShortTermGains,
    ytdLongTermGains,
    nextWithdrawal,
    isTaxMetricsLoading,
    isWithdrawalLoading,
    tradingPath,
}: TradingOverviewCardProps) => {
    const boosted = managementType === ManagementType.AUTOMATIC;
    const tradingActive = tradingStatus === TradingStatus.AUTOMATIC;
    const statusClassName = tradingActive
        ? 'bg-green-50 text-green-800'
        : tradingStatus === TradingStatus.SUSPENDED
          ? 'bg-red-50 text-red-600'
          : tradingStatus === TradingStatus.REBALANCE_IN_PROGRESS
            ? 'bg-blue-50 text-blue-700'
            : 'bg-stone-100 text-stone-600';
    const items = [
        {
            label: 'Portfolio Boost',
            icon: LuRocket,
            iconClassName: 'bg-cyan-50 text-teal-700',
            value: managementType ? (
                <span
                    className={`rounded px-2 py-1 text-[10px] font-medium tracking-wider uppercase ${
                        boosted ? 'bg-cyan-50 text-teal-700' : 'bg-stone-100 text-stone-600'
                    }`}
                >
                    {boosted ? 'Boosted' : 'Not enrolled'}
                </span>
            ) : (
                '—'
            ),
        },
        {
            label: 'Trading status',
            icon: LuArrowLeftRight,
            iconClassName: 'bg-blue-50 text-blue-700',
            value: tradingStatus ? (
                <span
                    className={`inline-flex items-center gap-1.5 rounded px-2 py-1 text-[10px] font-medium tracking-wider uppercase ${statusClassName}`}
                >
                    <span className="h-2 w-2 rounded-full bg-current" />
                    {tradingActive ? 'Trading active' : titleCase(tradingStatus)}
                </span>
            ) : (
                '—'
            ),
        },
        {
            label: 'Total Cash',
            icon: LuPiggyBank,
            iconClassName: 'bg-lime-50 text-green-600',
            value: cash === undefined ? '—' : formatCurrency(cash),
        },
        {
            label: 'Harvestable losses',
            icon: LuScissors,
            iconClassName: 'bg-blue-50 text-blue-700',
            value: harvestableLoss === undefined ? '—' : formatCurrency(harvestableLoss),
        },
        {
            label: 'YTD ST/LT Gains',
            icon: LuPercent,
            iconClassName: 'bg-green-50 text-green-600',
            value:
                isTaxMetricsLoading || (ytdShortTermGains === undefined && ytdLongTermGains === undefined)
                    ? '—'
                    : `${ytdShortTermGains === undefined ? '—' : formatCurrency(ytdShortTermGains)} ST · ${
                          ytdLongTermGains === undefined ? '—' : formatCurrency(ytdLongTermGains)
                      } LT`,
        },
        {
            label: 'Next Planned Withdrawal',
            icon: LuCalendarClock,
            iconClassName: 'bg-cyan-50 text-cyan-600',
            value:
                isWithdrawalLoading || !nextWithdrawal
                    ? '—'
                    : `${formatDate(nextWithdrawal.date, 'MMM d')} · ${formatCurrency(nextWithdrawal.amount)}`,
        },
    ];

    return (
        <Card className="border-border rounded-2xl bg-white p-4 lg:p-6">
            <div className="flex items-center justify-between gap-4">
                <h2 className="font-heading text-cocoa-1000 text-xl">Trading Overview</h2>
                <Button asChild variant="outline" size="icon" className="shrink-0 rounded-lg">
                    <Link to={tradingPath} aria-label="View account allocations and trading details">
                        <LuArrowUpRight className="h-5 w-5" />
                    </Link>
                </Button>
            </div>
            <div className="mt-4">
                {items.map(({ label, icon: Icon, iconClassName, value }) => (
                    <div key={label} className="border-border flex items-center gap-3 border-b py-3">
                        <span
                            className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${
                                managementType && !boosted ? 'bg-stone-100 text-stone-400' : iconClassName
                            }`}
                        >
                            <Icon className="h-4 w-4" />
                        </span>
                        <span className="text-cocoa-900 text-sm">{label}</span>
                        <span className="text-cocoa-1000 ml-auto text-right text-sm font-medium tabular-nums">
                            {value}
                        </span>
                    </div>
                ))}
            </div>
        </Card>
    );
};
